import React, { useState, useEffect, useRef } from "react";
import Default from "../../components/layouts/default";
import { Container, Row, Col, Form, Button, Card } from "react-bootstrap";
import {
  changePassword,
  getLastLogin,
  getProfilePictureBlob,
  uploadProfilePicture,
  getUserDetails,
  saveSmsConsent,
} from "../../services/profile.service";
import { useDispatch } from "react-redux";
import { setLoading } from "../../feature/loading.slice";
import { setMessage } from "../../feature/message.slice";
import messages from "../../utils/meesage";
import { formatDateTime } from "../../utils/dateFormat";
import LoginAuth from "../../services/auth.service";
import { useNavigate } from "react-router-dom";
import Error from "../../components/common/Error";
import Success from "../../components/common/Success";
import SmsConsentCheck from "../../components/common/SmsConsentCheck";

const MAX_FILE_SIZE_MB = 1; // Define the maximum file size

const Profile = () => {
  const [userDetails, setUserDetails] = useState({
    name: "",
    surname: "",
    email: "",
    phone: "",
    sms_consent: 0,
  });
  // The box as the client has it now; userDetails.sms_consent is what is saved.
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentMsg, setConsentMsg] = useState({ error: "", success: "" });
  const [savingConsent, setSavingConsent] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [lastLogin, setLastLogin] = useState("");
  const [profilePicture, setProfilePicture] = useState(null);
  const [preview, setPreview] = useState(null); // Stores selected image
  const [errorPassword, setErrorPassword] = useState(""); // Error message for password update
  const [successPassword, setSuccessPassword] = useState(""); // Success message for password update
  const [errorPicture, setErrorPicture] = useState(""); // Error message for profile picture
  const [successPicture, setSuccessPicture] = useState(""); // Success message for profile picture
  const [savedPreview, setSavedPreview] = useState(null); // the photo on file, to fall back to on Cancel
  const fileInput = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchUserDetails = async () => {
    try {
      dispatch(setLoading(true));
      const data = await getUserDetails();
      setUserDetails(data.user); // Populate user details dynamically
      setConsentChecked(!!data.user.sms_consent);
      dispatch(setLoading(false));
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  useEffect(() => {
    const fetchLastLogin = async () => {
      try {
        const data = await getLastLogin(userDetails.id);
        setLastLogin(data.lastLogin);
      } catch (error) {
        // No login on record yet answers 404; show a dash, not "Fetching..." forever.
        setLastLogin(false);
        console.error("Error fetching last login:", error);
      }
    };

    const fetchProfilePicture = async () => {
      try {
        const blob = await getProfilePictureBlob(userDetails.id);
        const url = URL.createObjectURL(blob); // Convert blob to a URL
        setPreview(url); // Set the preview URL for the image
        setSavedPreview(url);
      } catch (error) {
        console.error("Error fetching profile picture:", error);
      }
    };

    fetchUserDetails();
    fetchLastLogin();
    fetchProfilePicture();
  }, [userDetails.id, dispatch]);

  // Clear messages after 10 seconds
  const clearMessages = () => {
    setTimeout(() => {
      setErrorPassword("");
      setSuccessPassword("");
      setErrorPicture("");
      setSuccessPicture("");
    }, 10000);
  };


  // Handle password change
  const handleChangePassword = async () => {
    try {
     
      if (!currentPassword.trim() || !password.trim() || !confirmPassword.trim()) {
        setErrorPassword("Current Password, Password and Confirm Password fields cannot be empty.");
        setSuccessPassword("");
        clearMessages();
        return;
      }

      if (password !== confirmPassword) {
        setErrorPassword("Passwords do not match!");
        setSuccessPassword("");
        clearMessages();
        return;
      }

      dispatch(setLoading(true));
      dispatch(setMessage(messages.loading));

      await changePassword(userDetails.id, currentPassword, password, confirmPassword);
      setCurrentPassword("");
      setPassword("");
      setConfirmPassword("");
      setSuccessPassword("Password changed successfully.");
      setErrorPassword("");
      fetchUserDetails();
      dispatch(setMessage(messages.success.PASSWORD_RESET_SUCCESS));
      clearMessages();
    } catch (error) {
      // The API says what went wrong, including attempts left before lockout.
      setErrorPassword(error?.message || "Error changing password.");
      setSuccessPassword("");
      clearMessages();
      // Locked out: the API has already ended every session, this one too.
      if (error?.attemptsLeft === 0) {
        setTimeout(() => {
          LoginAuth.clearLocalSession();
          navigate("/login");
        }, 3000);
      }
    } finally {
      setTimeout(() => {
        dispatch(setLoading(false));
      }, 3000);
    }
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setErrorPicture(`File size cannot exceed ${MAX_FILE_SIZE_MB} MB.`);
        setSuccessPicture("");
        clearMessages();
        return;
      }
      setProfilePicture(file);
      setPreview(URL.createObjectURL(file)); // Generate a preview URL
    }
  };

  const handleUploadProfilePicture = async () => {
    try {
      if (!profilePicture) {
        setErrorPicture("Please select a profile picture first.");
        setSuccessPicture("");
        clearMessages();
        return;
      }
      dispatch(setLoading(true));
      dispatch(setMessage(messages.loading));
      const formData = new FormData();
      formData.append("file", profilePicture);

      await uploadProfilePicture(formData);
      setProfilePicture(null);
      setSavedPreview(preview);
      setSuccessPicture("Profile picture updated successfully.");
      setErrorPicture("");
      fetchUserDetails();
      dispatch(setMessage(messages.success.SAVE_FILE));
      clearMessages();
    } catch (error) {
      setErrorPicture("Error uploading profile picture.");
      setSuccessPicture("");
      clearMessages();
    } finally {
      setTimeout(() => {
        dispatch(setLoading(false));
      }, 3000);
    }
  };

  const handleSaveConsent = async () => {
    setSavingConsent(true);
    setConsentMsg({ error: "", success: "" });
    try {
      const res = await saveSmsConsent(consentChecked);
      setUserDetails({ ...userDetails, sms_consent: res.sms_consent });
      setConsentChecked(!!res.sms_consent);
      setConsentMsg({
        error: "",
        success: !consentChanged
          ? "Your preferences are saved."
          : res.sms_consent
            ? "Thanks - you've agreed to receive text messages at this number."
            : "You will no longer receive text messages from us.",
      });
    } catch (error) {
      setConsentMsg({ error: error?.message || "Could not save your choice.", success: "" });
    } finally {
      setSavingConsent(false);
    }
  };
  const consentChanged = consentChecked !== !!userDetails.sms_consent;

  const cancelPicture = () => {
    setProfilePicture(null);
    setPreview(savedPreview);
    if (fileInput.current) fileInput.current.value = "";
  };

  const fullName = [userDetails.name, userDetails.surname].filter(Boolean).join(" ");
  const initials = [userDetails.name, userDetails.surname]
    .filter(Boolean)
    .map((part) => part.trim()[0])
    .join("")
    .toUpperCase();

  const phoneDisplay = userDetails.phone
    ? (String(userDetails.phone).trim().startsWith("+")
        ? userDetails.phone
        : `${userDetails.country_code || ""} ${userDetails.phone}`.trim())
    : null;

  const fieldLabel = { fontSize: "0.78rem", letterSpacing: "0.02em" };


  return (
    <Default pageTitle="Profile">
      <Container className="mt-4" style={{ maxWidth: 960 }}>
        {/* Who you are: photo, name, last login */}
        <Card className="shadow-sm border-0 mb-4">
          <Card.Body className="p-4">
            <div className="d-flex align-items-center gap-4 flex-wrap">
              {/* The photo is the upload control: click to pick a new one. */}
              <button
                type="button"
                className="p-0 border-0 bg-transparent position-relative"
                onClick={() => fileInput.current?.click()}
                title="Change photo"
                style={{ width: 88, height: 88, flex: "0 0 auto", cursor: "pointer" }}
              >
                {preview ? (
                  <img
                    src={preview}
                    alt="Profile"
                    className="rounded-circle"
                    style={{
                      width: 88,
                      height: 88,
                      objectFit: "cover",
                      border: "3px solid #fff",
                      boxShadow: "0 0 0 1px #dee2e6, 0 4px 12px rgba(0, 0, 0, 0.15)",
                    }}
                  />
                ) : (
                  <span
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center fw-semibold"
                    style={{ width: 88, height: 88, fontSize: "1.8rem" }}
                  >
                    {initials || "?"}
                  </span>
                )}
                <span
                  className="position-absolute bg-white border rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                  style={{ width: 30, height: 30, right: -2, bottom: -2 }}
                  aria-hidden="true"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </span>
              </button>
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={handleProfilePictureChange}
              />

              <div className="flex-grow-1">
                <h4 className="mb-1">{fullName}</h4>
                <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                  Last logged in:{" "}
                  {lastLogin ? formatDateTime(lastLogin) : lastLogin === false ? "—" : "Fetching..."}
                </div>
                {profilePicture ? (
                  <div className="d-flex gap-2 mt-2">
                    <Button variant="primary" size="sm" onClick={handleUploadProfilePicture}>
                      Save photo
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={cancelPicture}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div className="text-muted mt-1" style={{ fontSize: "0.78rem" }}>
                    Click the photo to change it (max {MAX_FILE_SIZE_MB} MB).
                  </div>
                )}
                <Error error={errorPicture} />
                <Success message={successPicture} />
              </div>

            </div>
          </Card.Body>
        </Card>

        <Row className="g-4">
          {/* Contact info: read-only. 2FA codes go to this number, so only
              staff change it, from Teams. */}
          <Col md={5} className="d-flex">
            <Card className="flex-fill shadow-sm border-0">
              <Card.Body className="p-4 d-flex flex-column">
                <Card.Title className="mb-4">Contact info</Card.Title>

                {/* Read-only fields rather than plain text: this card is also
                    the SMS opt-in form carriers review, and a web-form opt-in
                    has to show the phone number field it applies to. */}
                <Form.Group controlId="profileEmail" className="mb-3">
                  <Form.Label className="text-muted text-uppercase mb-1" style={fieldLabel}>Email</Form.Label>
                  <Form.Control type="email" value={userDetails.email || ""} readOnly className="bg-light" />
                </Form.Group>

                <Form.Group controlId="profileMobile" className="mb-3">
                  <Form.Label className="text-muted text-uppercase mb-1" style={fieldLabel}>Mobile number</Form.Label>
                  <Form.Control
                    type="tel"
                    value={phoneDisplay || ""}
                    placeholder="No mobile number on file"
                    readOnly
                    className="bg-light"
                  />
                </Form.Group>

                {/* SMS opt-in for the number above. Optional and never ticked by
                    default; saving records when, from where and for which
                    number. The button is always shown, so the form reads as
                    complete with the box unticked. */}
                {phoneDisplay && (
                  <div className="mb-4 pt-3 border-top">
                    <div className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                      Text messages from ADM CPA P.C. <span className="text-muted fw-normal">(optional)</span>
                    </div>
                    <SmsConsentCheck
                      checked={consentChecked}
                      onChange={setConsentChecked}
                      disabled={savingConsent}
                    />
                    <Button
                      variant="primary"
                      size="sm"
                      className="mt-3"
                      onClick={handleSaveConsent}
                      disabled={savingConsent}
                    >
                      {savingConsent ? "Saving..." : "Save preferences"}
                    </Button>
                    <Error error={consentMsg.error} />
                    <Success message={consentMsg.success} />
                  </div>
                )}

                <div className="mt-auto pt-3 border-top text-muted" style={{ fontSize: "0.8rem" }}>
                  To change these, please contact your ADM One team.
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={7} className="d-flex">
            <Card className="flex-fill shadow-sm border-0">
              <Card.Body className="p-4">
                <Card.Title className="mb-4">Change password</Card.Title>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleChangePassword();
                  }}
                >
                  <Form.Group controlId="currentPassword" className="mb-3">
                    <Form.Label className="text-muted text-uppercase mb-1" style={fieldLabel}>
                      Current password
                    </Form.Label>
                    <Form.Control
                      type="password"
                      autoComplete="current-password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                  </Form.Group>
                  <Row className="g-3 mb-3">
                    <Col sm={6}>
                      <Form.Group controlId="password">
                        <Form.Label className="text-muted text-uppercase mb-1" style={fieldLabel}>
                          New password
                        </Form.Label>
                        <Form.Control
                          type="password"
                          autoComplete="new-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                    <Col sm={6}>
                      <Form.Group controlId="confirmPassword">
                        <Form.Label className="text-muted text-uppercase mb-1" style={fieldLabel}>
                          Confirm new password
                        </Form.Label>
                        <Form.Control
                          type="password"
                          autoComplete="new-password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <div className="d-flex justify-content-end">
                    <Button variant="primary" type="submit">
                      Change password
                    </Button>
                  </div>
                </Form>
                <Error error={errorPassword} />
                <Success message={successPassword} />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </Default>
  );
};

export default Profile;
